import { useEffect, useState } from 'react';
import { AppLayout } from '@/components/layout/app-layout';
import { PageHeader } from '@/components/patterns';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Plus, Plug, RefreshCw } from 'lucide-react';
import { useIntegrationStore } from '../store/integration-store';
import { IntegrationConfigCard } from '../components/integration-config-card';
import { IntegrationFormDialog } from '../components/integration-form-dialog';
import { SyncLogTable } from '../components/sync-log-table';
import { HubspotDataView } from '../components/hubspot-data-view';
import type { IntegrationConfig } from '../types/integration.types';

export function IntegrationsPage() {
  const { configs, logs, loading, fetchConfigs, fetchAllLogs } = useIntegrationStore();
  const [formOpen, setFormOpen] = useState(false);
  const [editConfig, setEditConfig] = useState<IntegrationConfig | null>(null);
  const [viewLogsConfig, setViewLogsConfig] = useState<IntegrationConfig | null>(null);

  useEffect(() => {
    fetchConfigs();
    fetchAllLogs();
  }, []);

  const handleEdit = (config: IntegrationConfig) => {
    setEditConfig(config);
    setFormOpen(true);
  };

  const handleCreate = () => {
    setEditConfig(null);
    setFormOpen(true);
  };

  const activeConfigs = configs.filter((c) => c.isActive);
  const inactiveConfigs = configs.filter((c) => !c.isActive);

  // If viewing logs for a specific config, filter
  const filteredLogs = viewLogsConfig
    ? logs.filter((l) => l.integrationId === viewLogsConfig.id)
    : logs;

  return (
    <AppLayout>
      {/* 19-oct: el encabezado y el ancho del rediseño; antes el título pegaba al menú. */}
      <div className="mx-auto max-w-[1440px] space-y-6 px-4 py-8 md:px-8">
        <PageHeader
          etiqueta="Integraciones"
          tonoEtiqueta="neutro"
          titulo="SAP y CRM"
          subtitulo="Configurá la sincronización con SAP S/4HANA, SAP C4C y HubSpot CRM."
          acciones={
            <>
              <Button
                variant="outline"
                onClick={() => {
                  fetchConfigs();
                  fetchAllLogs();
                }}
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Actualizar
              </Button>
              <Button variant="degradado" size="lg" className="px-5" onClick={handleCreate}>
                <Plus className="h-4 w-4" aria-hidden="true" />
                Nueva integración
              </Button>
            </>
          }
        />

        {/* Integration Cards */}
        {loading ? (
          <div className="text-center py-8 text-muted-foreground">Cargando...</div>
        ) : activeConfigs.length === 0 && inactiveConfigs.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Plug className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground mb-4">No hay integraciones configuradas</p>
              <Button onClick={handleCreate}>
                <Plus className="h-4 w-4 mr-2" />
                Configurar primera integración
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {activeConfigs.map((config) => (
              <IntegrationConfigCard
                key={config.id}
                config={config}
                onEdit={handleEdit}
                onViewLogs={setViewLogsConfig}
              />
            ))}
            {inactiveConfigs.map((config) => (
              <IntegrationConfigCard
                key={config.id}
                config={config}
                onEdit={handleEdit}
                onViewLogs={setViewLogsConfig}
              />
            ))}
          </div>
        )}

        {/* Sync Logs */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>
                {viewLogsConfig
                  ? `Historial de Sync \u2014 ${viewLogsConfig.name}`
                  : 'Historial de Sincronización'}
              </CardTitle>
              {viewLogsConfig && (
                <Button variant="ghost" size="sm" onClick={() => setViewLogsConfig(null)}>
                  Ver todos
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {viewLogsConfig?.type === 'HUBSPOT' ? (
              <HubspotDataView config={viewLogsConfig} logs={filteredLogs} />
            ) : (
              <SyncLogTable logs={filteredLogs} showIntegration={!viewLogsConfig} />
            )}
          </CardContent>
        </Card>

        {/* Form Dialog */}
        <IntegrationFormDialog open={formOpen} onOpenChange={setFormOpen} editConfig={editConfig} />
      </div>
    </AppLayout>
  );
}
